/**
 * Put research into a subject, anywhere in it.
 *
 * WHAT THIS REPLACED. Three dependent dropdowns — subject, then category, then
 * topic — each waiting on the one above. That shape encoded a three-level tree
 * into the UI, so when the tree stopped having three levels the control could
 * still only ever reach the third one: a node nested any deeper was
 * unreachable, and the most common way to widen a subject is to nest.
 *
 * So it is a subject, then ONE list of every node in it, in tree order with
 * indentation carrying the structure. The list logic is `pickRows`, which lives
 * in a dependency-free module because it is where this screen can quietly be
 * wrong — see its tests for the ancestor rule.
 *
 * Creating on the way in is kept, because the whole point of this modal is that
 * you found something before you had anywhere to put it. A new node lands under
 * whatever is selected, or at the top of the subject when nothing is.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import type { Project, TopicKeyword } from '../types';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { buildSubjectTree, pickRows } from '../pages/subjectTree';
import type { SubjectNode } from '../pages/subjectTree';
import { fetchAllPages } from '../services/paging';
import { apiErrorMessage } from '../services/errorMessage';

interface KeywordEntry {
  id: number;
  value: string;
}

interface AddToTopicModalProps {
  keywords: KeywordEntry[];
  initialProjectId?: number | null;
  initialCategoryId?: number | null;
  initialTopicId?: number | null;
  onClose: () => void;
  /** Called once the keywords are filed.
   *
   * `created` carries the TopicKeyword rows this run wrote, in the order the
   * keywords were given. Optional, and every existing caller ignores it — the
   * one caller that needs it (CreateFromProposal) has to know WHICH row it
   * landed on so it can hang a page off it, and looking that up afterwards
   * would be a guess whenever the same keyword sits under two nodes. */
  onSuccess: (created?: TopicKeyword[]) => void;
  /**
   * What the caller is actually doing, when it is not "filing a keyword".
   *
   * The Trends page reaches this modal from a **Create** button on a proposed
   * PAGE. Left to the defaults, that click opened a dialog headed "Add to a
   * subject" over a button reading "Add to Storage" — so the honest reading was
   * that a keyword had been filed, and then the app navigated to the page
   * builder for no stated reason. The destination question is the same; the
   * thing being answered is not.
   *
   * Optional, and every other caller keeps the defaults — for them "add this
   * keyword somewhere" IS the whole action.
   */
  purpose?: { title: string; blurb: string; verb: string };
}

export default function AddToTopicModal({
  keywords,
  initialProjectId,
  initialCategoryId,
  initialTopicId,
  onClose,
  onSuccess,
  purpose,
}: AddToTopicModalProps) {
  useEscapeKey(true, onClose);

  const [projects, setProjects] = useState<Project[]>([]);
  const [nodes, setNodes] = useState<SubjectNode[]>([]);
  const [loadingNodes, setLoadingNodes] = useState(false);

  const [projectId, setProjectId] = useState<number | null>(null);
  const [nodeKey, setNodeKey] = useState<number | null>(null);
  const [filter, setFilter] = useState('');

  const [creatingProject, setCreatingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [addingNode, setAddingNode] = useState(false);
  const [newNodeName, setNewNodeName] = useState('');
  const newNodeRef = useRef<HTMLInputElement>(null);

  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getProjects()
      .then((data) => {
        setProjects(data);
        if (initialProjectId && data.some((p) => p.id === initialProjectId)) {
          setProjectId(initialProjectId);
        }
      })
      .catch((e) => setError(apiErrorMessage(e)));
  }, [initialProjectId]);

  const loadNodes = useCallback(async () => {
    if (!projectId) {
      setNodes([]);
      return;
    }
    setLoadingNodes(true);
    try {
      // Paged, not capped: both list endpoints declare `limit: le=100`, so
      // asking for more is a 422 rather than a clamp — and a subject with 140
      // nodes would silently offer 100 of them.
      // One fetch: a root is a topic with no parent, so /topics is the whole
      // subject and the separate /categories call fetched rows already here.
      const tops = await fetchAllPages((skip, limit) =>
        api.getTopics(null, projectId, skip, limit),
      );
      setNodes(buildSubjectTree(tops));
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setLoadingNodes(false);
    }
  }, [projectId]);

  useEffect(() => {
    setNodeKey(null);
    setFilter('');
    setAddingNode(false);
    loadNodes();
  }, [loadNodes]);

  /**
   * Honour a caller that already knows where this belongs, once the tree loads.
   *
   * Both hints land on the same list now — `initialCategoryId` used to select a
   * dropdown that no longer exists, and it still arrives from Trending's
   * "add to this recommended topic" button, carrying a node id since the
   * migration. Dropping it would silently lose that pre-selection, so it is the
   * fallback rather than a separate control.
   */
  useEffect(() => {
    if (nodeKey !== null) return;
    // A topic hint is a node id; a category hint is a LEGACY id, which is a
    // different number — resolving it against the tree is the only way to land
    // on the right row.
    const match = initialTopicId
      ? nodes.find((n) => n.id === initialTopicId)
      : initialCategoryId
        ? nodes.find((n) => n.legacyCategoryId === initialCategoryId)
        : undefined;
    if (match) setNodeKey(match.id);
  }, [nodes, initialTopicId, initialCategoryId, nodeKey]);

  const rows = useMemo(() => pickRows(nodes, filter), [nodes, filter]);
  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === nodeKey) ?? null,
    [nodes, nodeKey],
  );

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    setError(null);
    try {
      const project = await api.createProject({ name: newProjectName.trim() });
      setProjects((prev) => [...prev, project]);
      setProjectId(project.id);
      setCreatingProject(false);
      setNewProjectName('');
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  };

  const handleCreateNode = async () => {
    const name = newNodeName.trim();
    if (!name || !projectId) return;
    setError(null);
    try {
      // `parent_id` reaches any node at any depth; the old `category_id` could
      // only ever address a root, which is what made this modal shallow.
      const created = await api.createTopic(
        selectedNode
          ? { name, parent_id: selectedNode.id }
          : { name, project_id: projectId },
      );
      setNewNodeName('');
      setAddingNode(false);
      setFilter('');
      await loadNodes();
      setNodeKey(created.id);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  };

  const handleConfirm = async () => {
    if (!selectedNode) return;
    setSubmitting(true);
    setError(null);
    setProgress(0);
    const errors: string[] = [];
    const created: TopicKeyword[] = [];
    for (let i = 0; i < keywords.length; i++) {
      try {
        created.push(
          await api.createTopicKeyword({
            topic_id: selectedNode.id,
            keyword_id: keywords[i].id,
          }),
        );
      } catch (e) {
        errors.push(`"${keywords[i].value}": ${apiErrorMessage(e)}`);
      }
      setProgress(i + 1);
    }
    if (errors.length > 0 && errors.length === keywords.length) {
      setError(`Failed to add all keywords: ${errors.join('; ')}`);
      setSubmitting(false);
    } else if (errors.length > 0) {
      setError(
        `Added ${keywords.length - errors.length}/${keywords.length}. Errors: ${errors.join('; ')}`,
      );
      setTimeout(() => onSuccess(created), 2000);
    } else {
      onSuccess(created);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6 flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
          {purpose?.title ?? 'Add to a subject'}
        </h2>
        {purpose ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {purpose.blurb}
          </p>
        ) : keywords.length === 1 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 truncate">
            Keyword:{' '}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {keywords[0].value}
            </span>
          </p>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {keywords.length} keywords
            </span>{' '}
            selected
          </p>
        )}

        {error && (
          <div className="mb-3 p-2 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 rounded">
            {error}
          </div>
        )}

        {/* Subject */}
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Subject
          </label>
          {creatingProject ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateProject()}
                placeholder="Subject name"
                aria-label="New subject name"
                className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoFocus
              />
              <button
                onClick={handleCreateProject}
                disabled={!newProjectName.trim()}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create
              </button>
              <button
                onClick={() => setCreatingProject(false)}
                className="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <select
                value={projectId ?? ''}
                onChange={(e) => setProjectId(e.target.value ? Number(e.target.value) : null)}
                aria-label="Subject"
                className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Choose a subject…</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => setCreatingProject(true)}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                + New
              </button>
            </div>
          )}
        </div>

        {/* Node — one list, any depth */}
        {projectId && (
          <div className="mb-3 min-h-0 flex flex-col">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Where in it
            </label>
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter nodes…"
              aria-label="Filter nodes"
              className="mb-2 w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <div className="flex-1 min-h-0 max-h-56 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded">
              {loadingNodes && (
                <p className="px-3 py-3 text-sm text-gray-400 dark:text-gray-500">Loading…</p>
              )}
              {!loadingNodes && rows.length === 0 && (
                <p className="px-3 py-3 text-sm text-gray-400 dark:text-gray-500">
                  {filter
                    ? 'Nothing matches that filter.'
                    : 'Nothing in this subject yet - add the first node below.'}
                </p>
              )}
              {rows.map(({ node, depth }) => (
                <button
                  key={node.id}
                  onClick={() => setNodeKey(node.id)}
                  style={{ paddingLeft: 12 + depth * 16 }}
                  className={`w-full text-left pr-3 py-1.5 text-sm truncate ${
                    node.id === nodeKey
                      ? 'bg-blue-50 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 font-medium'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {node.name}
                </button>
              ))}
            </div>

            {addingNode ? (
              /* Enter still works, but it is no longer the ONLY way through.
                 A bare text box with no button says "type something", not "and
                 then press Enter to commit it" — and the subject input directly
                 above this has had a Create button all along, so the one that
                 did not was also the odd one out. */
              <div className="mt-2 flex gap-2">
                <input
                  ref={newNodeRef}
                  type="text"
                  value={newNodeName}
                  onChange={(e) => setNewNodeName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateNode();
                    if (e.key === 'Escape') {
                      setAddingNode(false);
                      setNewNodeName('');
                    }
                  }}
                  placeholder={
                    selectedNode ? `Name, under ${selectedNode.name}` : 'Name of the new section'
                  }
                  aria-label="New node name"
                  className="flex-1 border border-blue-400 dark:border-blue-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoFocus
                />
                <button
                  onClick={handleCreateNode}
                  disabled={!newNodeName.trim()}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  Create
                </button>
                <button
                  onClick={() => {
                    setAddingNode(false);
                    setNewNodeName('');
                  }}
                  className="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  setAddingNode(true);
                  setTimeout(() => newNodeRef.current?.focus(), 0);
                }}
                className="mt-2 w-full px-3 py-1.5 text-sm text-left text-blue-700 dark:text-blue-300 border border-dashed border-gray-300 dark:border-gray-600 rounded hover:border-blue-400 dark:hover:border-blue-600"
              >
                + New node{selectedNode ? ` under ${selectedNode.name}` : ''}
              </button>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedNode || submitting}
            className="px-4 py-1.5 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting
              ? `Adding ${progress}/${keywords.length}…`
              : selectedNode
                ? `${purpose?.verb ?? 'Add to'} ${selectedNode.name}`
                : (purpose?.verb ?? 'Add')}
          </button>
        </div>
      </div>
    </div>
  );
}
